import {
    DotLegalAlert,
    DotLegalCheckbox,
    DotLegalDialog,
    DotLegalHeader,
    DotLegalLink,
    DotLegalMultiSelect,
    DotLegalSelect,
    DotLegalTextField,
} from "@dotlegal/dotlegal-ui-components";
import React from "react";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { isTemplateCustomer } from "../../common/templateCustomerHelper";
import { useTranslation } from "../../localization/useTranslation";
import { useAddSystemDialogDataMapping } from "./AddSystemDialog.hooks";
import TrialUsageWarning from "../../common/components/TrialUsageWarning.tsx";
import { Alert, AlertTitle, Box } from "@mui/material";
import { DotLegalSelectOption } from "../../common/components/dotLegalMultiSelect/DotLegalMultiSelect.types";
import { Trans } from "react-i18next";
import { SystemStandardFieldRenderer } from "../../entityTemplates/SystemFieldRenderers.tsx";
import { SystemCustomFieldRenderer } from "../../entityTemplates/SystemFieldRenderers.tsx";
import { SystemModel } from "../systemEdit/SystemEdit.types.ts";
import TagsMultiSelect from "../../common/components/tagsMultiSelect/TagsMultiSelect.tsx";
import { TagRelationType } from "../../common/components/tagsMultiSelect/TagsMultiSelect.types.ts";
import { SystemStandardField, getStandardFieldTranslationKey } from "../../entityTemplates/EntityTemplate.types.ts";
import SelectableLegalEntityMultiSelect from "../../legalEntity/SelectableLegalEntityMultiSelect.tsx";
import SystemAssociatedAssetsCreationField from "../creationFields/SystemAssociatedAssetsCreationField.tsx";
import SystemDataSubjectsCreationField from "../creationFields/SystemDataSubjectsCreationField.tsx";
import SystemDataCategoriesCreationField from "../creationFields/SystemDataCategoriesCreationField.tsx";
import SystemSecurityMeasuresCreationField from "../creationFields/SystemSecurityMeasuresCreationField.tsx";
import SystemPoliciesCreationField from "../creationFields/SystemPoliciesCreationField.tsx";

export interface IAddSystemDialogProps {
    showDialog: boolean;
    onHideDialog: () => void;
    onSystemCreated: (id: string) => void;
    searchedName?: string;
    shouldDisplayCreateSystemWarning?: boolean;
}

function AddSystemDialog(props: IAddSystemDialogProps) {
    const {
        createSystem,
        inProgress,
        validation,
        systemModel,
        setSystemModel,
        templateTypes,
        templates,
        templatesIsLoading,
        showInfoSecUpgradeAlert,
        onDismissInfoSecAlert,
        selectedTemplateMissingAccess,
        hasRequiredFields,
        hasStandardRequiredFields,
        standardRequiredFields,
        customRequiredFields,
        tagsRequiredAtCreation,
        selectedTagIds,
        setSelectedTagIds,
        requiredFieldValidationErrors,
        editedSystem,
        handleRequiredStandardFieldChange,
        handleRequiredCustomFieldChange,
        onTemplateChange,
        permissions,
        suppliersRequiredAtCreation,
        associatedAssetsRequiredAtCreation,
        dataSubjectsRequiredAtCreation,
        dataCategoriesRequiredAtCreation,
        selectedSupplierIds,
        setSelectedSupplierIds,
        selectedAssetIds,
        setSelectedAssetIds,
        selectedDataSubjectIds,
        setSelectedDataSubjectIds,
        selectedDataCategoryIds,
        setSelectedDataCategoryIds,
        securityMeasuresRequiredAtCreation,
        selectedSecurityMeasureIds,
        setSelectedSecurityMeasureIds,
        policiesRequiredAtCreation,
        selectedPolicyIds,
        setSelectedPolicyIds,
    } = useAddSystemDialogDataMapping(props);
    const { translateString } = useTranslation();
    const { language } = useUserContext();

    validation.check(systemModel);

    return (
        <DotLegalDialog
            inProgress={inProgress}
            size="sm"
            buttonOkText={translateString("create")}
            header={translateString("createSystem")}
            onOkClick={async () => {
                createSystem();
            }}
            onDialogClose={props.onHideDialog}
            open={props.showDialog}
            disableBackdropOnCloseIfValueChange={systemModel}
            okButtonDisabled={selectedTemplateMissingAccess}
        >
            <Box sx={{ width: "95%" }}>
                <DotLegalTextField
                    autoFocus
                    debounce={false}
                    size="small"
                    label={translateString("system")}
                    value={systemModel.name}
                    onChange={(x) => {
                        let tempModel = { ...systemModel };
                        tempModel.name = x;
                        setSystemModel(tempModel);
                    }}
                    errorText={validation?.getErrorsForDisplay("name")}
                />

                <DotLegalSelect
                    options={templates}
                    onChange={(template) => {
                        onTemplateChange(template);
                    }}
                    label={translateString("assetTemplate")}
                    noOptionsLabel={translateString("noOptions")}
                    isLoading={templatesIsLoading}
                    toolTipText={translateString("addSystemTemplateTooltip")}
                    disableClearable
                    selectedItem={systemModel.entityTemplateId}
                    errorText={validation?.getErrorsForDisplay("entityTemplateId")}
                />

                {selectedTemplateMissingAccess && (
                    <Box sx={{ marginTop: 2 }}>
                        <DotLegalAlert severity="info" warningText={translateString("missingRequiredAtCreationFieldAccess")} />
                    </Box>
                )}

                {!selectedTemplateMissingAccess && hasRequiredFields && systemModel.entityTemplateId && (
                    <>
                        {hasStandardRequiredFields && (
                            <>
                                <DotLegalHeader headerStyle="small">{translateString("standardFields")}</DotLegalHeader>
                                {standardRequiredFields.map((field) => (
                                    <Box key={field.id} sx={{ marginBottom: 2 }}>
                                        <SystemStandardFieldRenderer
                                            field={field}
                                            system={editedSystem as SystemModel}
                                            systemId=""
                                            onChange={handleRequiredStandardFieldChange}
                                            onRefetchSystem={() => {}}
                                            validationErrors={requiredFieldValidationErrors}
                                            isLoading={false}
                                            isPreview={false}
                                            disableAccessWarning
                                        />
                                    </Box>
                                ))}
                                {tagsRequiredAtCreation && (
                                    <TagsMultiSelect
                                        selectedTags={selectedTagIds}
                                        onChange={setSelectedTagIds}
                                        entityType={TagRelationType.System}
                                        isLoading={false}
                                        errorText={
                                            requiredFieldValidationErrors.find(
                                                (v) => v.field === getStandardFieldTranslationKey(SystemStandardField.SystemTags)
                                            )?.error
                                        }
                                    />
                                )}
                                {suppliersRequiredAtCreation && (
                                    <SelectableLegalEntityMultiSelect
                                        selectedIds={selectedSupplierIds}
                                        onChange={setSelectedSupplierIds}
                                        label={translateString("suppliers")}
                                        errorText={
                                            requiredFieldValidationErrors.find(
                                                (v) => v.field === getStandardFieldTranslationKey(SystemStandardField.SystemSuppliers)
                                            )?.error
                                        }
                                    />
                                )}
                                {associatedAssetsRequiredAtCreation && (
                                    <SystemAssociatedAssetsCreationField
                                        selectedIds={selectedAssetIds}
                                        onChange={setSelectedAssetIds}
                                        disabled={false}
                                        errorText={
                                            requiredFieldValidationErrors.find(
                                                (v) => v.field === getStandardFieldTranslationKey(SystemStandardField.SystemAssociatedAssets)
                                            )?.error
                                        }
                                    />
                                )}
                                {dataSubjectsRequiredAtCreation && (
                                    <SystemDataSubjectsCreationField
                                        selectedIds={selectedDataSubjectIds}
                                        onChange={setSelectedDataSubjectIds}
                                        disabled={false}
                                        errorText={
                                            requiredFieldValidationErrors.find(
                                                (v) => v.field === getStandardFieldTranslationKey(SystemStandardField.SystemDataSubjects)
                                            )?.error
                                        }
                                    />
                                )}
                                {dataCategoriesRequiredAtCreation && (
                                    <SystemDataCategoriesCreationField
                                        selectedIds={selectedDataCategoryIds}
                                        onChange={setSelectedDataCategoryIds}
                                        disabled={false}
                                        errorText={
                                            requiredFieldValidationErrors.find(
                                                (v) => v.field === getStandardFieldTranslationKey(SystemStandardField.SystemDataCategories)
                                            )?.error
                                        }
                                    />
                                )}
                                {securityMeasuresRequiredAtCreation && (
                                    <SystemSecurityMeasuresCreationField
                                        selectedIds={selectedSecurityMeasureIds}
                                        onChange={setSelectedSecurityMeasureIds}
                                        disabled={false}
                                        errorText={
                                            requiredFieldValidationErrors.find(
                                                (v) => v.field === getStandardFieldTranslationKey(SystemStandardField.SystemSecurityMeasures)
                                            )?.error
                                        }
                                    />
                                )}
                                {policiesRequiredAtCreation && (
                                    <SystemPoliciesCreationField
                                        selectedIds={selectedPolicyIds}
                                        onChange={setSelectedPolicyIds}
                                        disabled={false}
                                        errorText={
                                            requiredFieldValidationErrors.find(
                                                (v) => v.field === getStandardFieldTranslationKey(SystemStandardField.SystemPolicies)
                                            )?.error
                                        }
                                    />
                                )}
                            </>
                        )}
                        {customRequiredFields.length > 0 && (
                            <>
                                <DotLegalHeader headerStyle="small">{translateString("customFields")}</DotLegalHeader>
                                {customRequiredFields.map((field) => (
                                    <Box key={field.id} sx={{ marginBottom: 2 }}>
                                        <SystemCustomFieldRenderer
                                            field={field}
                                            system={editedSystem as SystemModel}
                                            systemId=""
                                            onChange={handleRequiredCustomFieldChange}
                                            validationErrors={requiredFieldValidationErrors}
                                            isLoading={false}
                                            isPreview={false}
                                        />
                                    </Box>
                                ))}
                            </>
                        )}
                    </>
                )}

                {showInfoSecUpgradeAlert && (
                    <Alert sx={{ marginTop: 2 }} severity="info" onClose={onDismissInfoSecAlert}>
                        <AlertTitle sx={(theme) => ({ fontSize: theme.typography.pxToRem(14) })}>{translateString("upgradeToInformationSecurity")}</AlertTitle>
                        <Box component={"span"} sx={(theme) => ({ fontSize: theme.typography.pxToRem(12) })}>
                            <Trans
                                i18nKey={"upgradeToInformationSecurityInfo"}
                                components={{
                                    plan: (
                                        <DotLegalLink
                                            bold
                                            to={
                                                language === "da"
                                                    ? "https://www.dotlegal.com/da/produkt/informations-sikkerheds-software"
                                                    : "https://www.dotlegal.com/en/product/information-security-management-software"
                                            }
                                            linkColor="primary"
                                        >
                                            {undefined}
                                        </DotLegalLink>
                                    ),
                                }}
                            />
                        </Box>
                    </Alert>
                )}

                {permissions.canManageCustomers && isTemplateCustomer() && (
                    <React.Fragment>
                        <DotLegalMultiSelect
                            options={templateTypes}
                            chosenOptions={systemModel.systemTypes.map((x) => {
                                return x.toString();
                            })}
                            placeholder={"Type"}
                            label={"Type"}
                            onChange={(types) => {
                                let tempModel = { ...systemModel };
                                tempModel.systemTypes = types;
                                setSystemModel(tempModel);
                            }}
                            noOptionsLabel={translateString("noOptions")}
                        />

                        <DotLegalCheckbox
                            label={"Mest populær?"}
                            checked={systemModel.mostPopular}
                            onChange={(mostPopular) => {
                                let tempViewModel = { ...systemModel! };
                                tempViewModel.mostPopular = mostPopular;
                                setSystemModel(tempViewModel);
                            }}
                        />
                    </React.Fragment>
                )}

                {props.shouldDisplayCreateSystemWarning && <TrialUsageWarning textKey={"createExceedingNumberOfSystemWarning"} marginTop={3} />}
            </Box>
        </DotLegalDialog>
    );
}

export default AddSystemDialog;
